import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { authAPI } from '@/lib/api';
import { Sparkles, ArrowLeft, Award, Plus, Trophy, Star, Zap, Crown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const Badges = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [newBadgeName, setNewBadgeName] = useState('');

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      const userData = await authAPI.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Load user error:', error);
    }
  };

  const handleAddBadge = async () => {
    if (!newBadgeName.trim()) {
      toast.error('Please enter a badge name');
      return;
    }

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(`${BACKEND_URL}/api/badges/award`, null, {
        params: { badge_name: newBadgeName.trim().toLowerCase().replace(/ /g, '_') },
        withCredentials: true
      });
      toast.success('Badge awarded!');
      setNewBadgeName('');
      loadUser();
    } catch (error) {
      console.error('Award badge error:', error);
      toast.error('Failed to award badge');
    }
  };

  const getBadgeIcon = (badge) => {
    if (badge.includes('master') || badge.includes('expert')) return <Crown className="h-5 w-5" />;
    if (badge.includes('star') || badge.includes('top')) return <Star className="h-5 w-5" />;
    if (badge.includes('streak')) return <Zap className="h-5 w-5" />;
    if (badge.includes('first')) return <Trophy className="h-5 w-5" />;
    return <Award className="h-5 w-5" />;
  };

  const allPossibleBadges = [
    { id: 'first_prompt', name: 'First Prompt', description: 'Created your first prompt' },
    { id: 'daily_streak_7', name: '7-Day Streak', description: 'Completed challenges for 7 days straight' },
    { id: 'master_prompter', name: 'Master Prompter', description: 'Achieved mastery level' },
    { id: 'community_star', name: 'Community Star', description: 'Contributed valuable prompts' }
  ];

  return (
    <div className="min-h-screen">
      <nav className="px-6 py-4 bg-white/95 backdrop-blur-sm border-b border-border shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <Sparkles className="h-7 w-7 text-primary" />
          <h1 className="text-xl font-semibold text-foreground">Your Badges</h1>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <h2 className="text-4xl font-bold text-foreground mb-3">Achievement <span className="text-primary">Badges</span></h2>
        <p className="text-lg text-muted-foreground mb-8">Collect badges as you master prompt engineering</p>

        <Card className="p-6 bg-white border border-border card-shadow-lg mb-8">
          <h3 className="text-xl font-semibold mb-4 text-foreground">Award Yourself a Badge</h3>
          <div className="flex gap-3">
            <Input 
              value={newBadgeName}
              onChange={(e) => setNewBadgeName(e.target.value)}
              placeholder="Enter badge name (e.g., Master Prompter)"
              className="flex-1 border border-border"
              onKeyPress={(e) => e.key === 'Enter' && handleAddBadge()}
            />
            <Button 
              onClick={handleAddBadge}
              className="bg-accent text-white shadow-md hover:shadow-lg font-semibold"
            >
              <Plus className="mr-2 h-4 w-4" /> Add Badge
            </Button>
          </div>
        </Card>

        <div className="mb-8">
          <h3 className="text-2xl font-semibold mb-4 text-foreground">Your Earned Badges ({user?.badges?.length || 0})</h3>
          {user?.badges && user.badges.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {user.badges.map((badge, index) => (
                <Card key={index} className="p-6 bg-gradient-to-br from-accent/5 to-primary/5 border border-border card-shadow-lg">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0 border-2 border-accent/20">
                      {getBadgeIcon(badge)}
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground capitalize">{badge.replace(/_/g, ' ')}</h4>
                      <p className="text-xs text-muted-foreground mt-1">Achievement unlocked</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-12 bg-white border border-border card-shadow-lg text-center">
              <Award className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No badges earned yet. Start learning to earn badges!</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Badges;
